import React, { FC } from 'react';
import { DefaultTheme } from 'styled-components';

import { Pagination, unescapeChars } from '@crehana/ui';
import { ArrowIosUp } from '@crehana/icons/outline';
import { useMe } from '@crehana/web/dist/ReactContexts/AuthContext';
import { useTranslation } from '@crehana/i18n';
import { ClientSideComponent } from '@crehana/web';

import Media from 'Jsx/Shared/ui/Media';
import {
  getCategorySelected,
  getLevelsSelected,
  getSoftwaresSelected,
} from 'Jsx/Catalog/components/FiltersDesktop/utils';
import CourseRecommended from 'Jsx/Catalog/components/CourseRecommended';
import { useAppContext } from 'Jsx/Catalog/context/AppContext';
import useCatalogCoursesQuery from 'Jsx/Catalog/hooks/useCatalogCoursesQuery';
import CatalogAcademies from 'Jsx/Catalog/components/CatalogAcademies';

import CourseList from '../CourseList/CourseList';
import CourseListPremium from '../CourseList/CourseListPremium';
import BannerPremiumTrialDesktop from '../BannerPremiumTrial/Desktop';
import OrderByDesktop from '../OrderByDesktop/OrderByDesktop';
import FilterChips from '../FilterChips';
import CommonSkeleton from '../CourseList/CommonSkeleton';
import SliderHeader from '../SliderHeader';

import EmptyResults from './components/EmptyResults';

interface ResultsV2Props {
  cleanFiltersMsg: string;
  shouldRenderCourseFreeBanner?: boolean;
  scrollTop: () => void;
  seoLongDescription: string | null;
  shouldRenderSliderHeader: boolean;
  allCourses: boolean;
}

const Results: FC<ResultsV2Props> = ({
  scrollTop,
  cleanFiltersMsg,
  shouldRenderCourseFreeBanner,
  seoLongDescription,
  shouldRenderSliderHeader,
  allCourses,
}) => {
  const {
    filters,
    orderBy: { itemSelected },
    currentPage,
    filterActions,
    setCurrentPage,
  } = useAppContext();
  const { categorySelected, subCategoriesSelected } =
    getCategorySelected(filters);
  const levelsSelected = getLevelsSelected(filters);
  const softwareSelected = getSoftwaresSelected(filters);
  const { me: user, loading: loadingMe } = useMe();
  const { t } = useTranslation();

  const { courses, totalCount, totalPages, loading } = useCatalogCoursesQuery();

  const resultTitleStyled = React.useMemo<{
    styled?: JSX.Element;
    plain: string;
  }>(() => {
    const styleTitle = (leftText: string, rightText?: string) => (
      <>
        {leftText}&nbsp;
        {rightText && <span className="text-primary-main">{rightText}</span>}
      </>
    );

    if (subCategoriesSelected.length > 0) {
      return {
        plain: t('CATALOG_COURSE_OF_X_TITLE', {
          interpolation: {
            name: subCategoriesSelected[0]!.name,
          },
        }),
        styled: styleTitle(
          t('CATALOG_COURSE_OF_TITLE'),
          subCategoriesSelected[0]!.name,
        ),
      };
    }

    if (categorySelected) {
      return {
        plain: `Cursos de ${categorySelected.name}`,
        styled: styleTitle(t('CATALOG_COURSE_OF_TITLE'), categorySelected.name),
      };
    }

    if (softwareSelected.length > 0) {
      const softwareTitle = softwareSelected
        .map(software => software.name)
        .join(', ');

      return {
        plain: t('CATALOG_COURSE_OF_X_TITLE', {
          interpolation: {
            name: softwareTitle,
          },
        }),
        styled: styleTitle(t('CATALOG_COURSE_OF_TITLE'), softwareTitle),
      };
    }

    if (levelsSelected.length > 0) {
      const levelsTitle = levelsSelected.map(l => l.name).join(', ');

      return {
        plain: t('CATALOG_COURSE_OF_X_TITLE', {
          interpolation: {
            name: levelsTitle,
          },
        }),
        styled: styleTitle(t('CATALOG_COURSE_OF_TITLE'), levelsTitle),
      };
    }

    if (itemSelected && itemSelected.slug === 'online') {
      const allCoursesMessage = t('ALL_COURSES');

      return {
        plain: allCoursesMessage,
        styled: styleTitle(allCoursesMessage),
      };
    }

    if (itemSelected && itemSelected.slug === 'ofertas') {
      return {
        plain: `Cursos en oferta`,
        styled: undefined,
      };
    }

    return {
      plain:
        (itemSelected &&
          itemSelected.slug === 'ofertas' &&
          t('CATALOG_COURSE_IN_X_TITLE', {
            interpolation: {
              name: itemSelected.value,
            },
          })) ||
        (itemSelected && t(itemSelected.label)),
      styled: undefined,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    categorySelected,
    itemSelected,
    levelsSelected,
    softwareSelected,
    subCategoriesSelected,
  ]);

  const onPageChange = (page: number) => {
    setCurrentPage(page);
    scrollTop();
  };

  const isEmptyCourseList = !loading && (!courses || !courses.length);
  const isStaff = !loadingMe && !!user?.isStaff;
  const isProfessor = !loadingMe && !!user?.isProfessor;
  const isB2B = !!user?.isB2B;
  const showVisualRest =
    !isEmptyCourseList &&
    courses.length > 10 &&
    !isStaff &&
    !isProfessor &&
    !isB2B;
  const isPremium = !!user?.isPremium;
  const isPremiumTrial = !!user?.isPremiumTrial;
  const isPayg = !!user?.isPAYG;

  // Academies validation. No render if user is in Brazil.
  const shouldRenderPremiumAcademySection =
    isPremium && !isPremiumTrial && !isB2B;
  const shouldRenderPaygB2BAcademySection = isB2B || isPayg;
  const currentCategorySlug = categorySelected?.slug
    ? [categorySelected.slug]
    : undefined;

  const renderAcademySection = () => {
    const customTitle =
      currentCategorySlug?.length && categorySelected?.name
        ? t('CATALOG_ACADEMIES_SECTION_TITLE', {
            interpolation: {
              categoryName: categorySelected.name,
            },
          })
        : undefined;

    return (
      <ClientSideComponent>
        <CatalogAcademies
          title={customTitle}
          sourceDetail="Results"
          currentCategorySlug={currentCategorySlug}
        />
      </ClientSideComponent>
    );
  };

  const renderCourses = () => {
    if (loading)
      return <CommonSkeleton isPremium={isPremium} skeletonItems={24} />;

    return isPremium || isB2B ? (
      <CourseListPremium
        skeletonItems={24}
        courses={courses}
        loading={loading}
        user={user}
        showVisualRest={showVisualRest}
      />
    ) : (
      <CourseList
        skeletonItems={24}
        courses={courses}
        loading={loading}
        showVisualRest={showVisualRest}
        withPriority
      />
    );
  };

  return (
    <main
      className="flex flex-col w-full"
      css={`
        ${({ theme }: { theme: DefaultTheme }) => theme.breakpoints.up('lg')} {
          width: calc(100% - 312px);
        }
      `}
    >
      {shouldRenderSliderHeader && (
        <Media from="md" defaultMatches>
          <SliderHeader className="hidden mb-40 md:block" />
        </Media>
      )}

      {shouldRenderPremiumAcademySection && (
        <div>
          {renderAcademySection()}
          <div className="w-full h-1 mb-32 border border-solid border-gray-main" />
        </div>
      )}

      <div className="w-full">
        <div className="w-full bg-white sm:mb-24">
          <div className="mt-0 mb-12 text-left sm:mb-24">
            <h1 className="inline tw-font-semibold text-24 sm:text-32 leading-32 tw-font-pop">
              {resultTitleStyled.styled || resultTitleStyled.plain}
            </h1>
          </div>

          {!loading && (
            <div className="mb-8 sm:mb-0">
              <Media only="xs">
                <FilterChips hideTitle hideCleanButton />
              </Media>

              <div className="flex items-center justify-between w-full">
                {!allCourses && (
                  <span
                    className="tw-font-semibold font-subtitle3 text-gray-dark"
                    aria-hidden
                  >
                    {totalCount}{' '}
                    {totalCount && (totalCount > 1 || totalCount === 0)
                      ? t('TOTAL_LABEL_2')
                      : t('TOTAL_LABEL')}
                  </span>
                )}

                <Media from="sm">
                  <OrderByDesktop />
                </Media>
              </div>
            </div>
          )}
        </div>

        {isEmptyCourseList && (
          <div className="w-full">
            {shouldRenderCourseFreeBanner && (
              <Media from="sm">
                <BannerPremiumTrialDesktop />
              </Media>
            )}

            <EmptyResults
              notFoundDescription={resultTitleStyled.plain}
              cleanFiltersMsg={cleanFiltersMsg}
              onClearFilters={() => {
                filterActions.clearAllFilters();
              }}
            />

            <CourseRecommended />
          </div>
        )}

        {renderCourses()}

        {totalPages > 1 ? (
          <div className="flex flex-col items-center lg:flex-row lg:justify-end">
            <button
              type="button"
              className="flex items-center my-8 cursor-pointer sm:mr-24"
              onClick={scrollTop}
            >
              <ArrowIosUp
                size={24}
                ignoreColor
                className="fill-current sm:mr-8 text-secondary-main"
              />
              <span className="font-link-2 text-secondary-main">
                {t('BACK_TO_TOP')}
              </span>
            </button>
            <Pagination
              type="alternative"
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={onPageChange}
              className="my-8"
            />
          </div>
        ) : null}

        {shouldRenderPaygB2BAcademySection && (
          <>
            <div className="w-full h-1 my-32 border border-solid border-gray-main" />

            {renderAcademySection()}
          </>
        )}

        {seoLongDescription && (
          <>
            <h3 className="mt-32 mb-16 font-subtitle3 text-gray-dark">
              Explora los {resultTitleStyled.plain}
            </h3>
            <p
              className="whitespace-pre-line font-legals text-gray-dark"
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{
                __html: unescapeChars(seoLongDescription),
              }}
            />
          </>
        )}
      </div>
    </main>
  );
};

export default Results;
